/**
 * GraphNode - Generic node renderer for xyflow
 *
 * Displays endpoints as rows with handles aligned to each row.
 */

import { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { useUpdateNodeInternals as useUpdateReactFlowHandles, useEdges } from '@xyflow/react';
import { getVisualization, resolveVizType, subscribeToRegistry } from './VisualizationRegistry';
import { EndpointRow, ROW_HEIGHT } from '../endpoint/EndpointRow';
import { sortEndpoints, groupEndpoints } from '../../shared/utils';
import { GraphNodeHeader, HEADER_HEIGHT } from './GraphNodeHeader';
import { NodeState } from '@/runtime/RuntimeStore';

import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { DEFAULT_VIZ_HEIGHT, NodeVisualization } from './NodeVisualization';

import { CompactErrorBoundary } from './CompactErrorBoundary';




// Data shape for our ReactFlow nodes
export interface GraphNodeData extends Record<string, unknown> {
  instanceId: string;
  node: NodeState;
  isCollapsed?: boolean;
}

interface GraphNodeProps {
  data: GraphNodeData;
}

const NODE_WIDTH = 200;


function GraphNodeComponent({ data, id, selected }: GraphNodeProps & { id: string; selected?: boolean }) {
  const { instanceId, node, isCollapsed: isNodeCollapsed } = data;
  const { setNodeCollapsed, getDefinition, definitionState } = useRuntime();

  // --- All hooks must be called unconditionally (React rules of hooks) ---
  const [registryVersion, setRegistryVersion] = useState(0);
  useEffect(() => {
    return subscribeToRegistry(() => setRegistryVersion(n => n + 1));
  }, []);

  const vizNode = definitionState[node.type]?.vizNode;
  const vizType = useMemo(() => resolveVizType(node.type, getDefinition), [node.type, getDefinition, registryVersion, vizNode]);
  const vizRegistration = vizType ? getVisualization(vizType) : null;
  const [vizHeight, setVizHeight] = useState(!!vizRegistration ? DEFAULT_VIZ_HEIGHT : 0);
  const edges = useEdges();

  const toggleNodeCollapsed = useCallback(() => {
    setNodeCollapsed(instanceId, node.id, !isNodeCollapsed);
  }, [setNodeCollapsed, instanceId, node.id, isNodeCollapsed]);

  const connectedEndpoints = useMemo(() => {
    const connected = new Set<string>();
    for (const edge of edges) {
      if (edge.source === id && edge.sourceHandle) {
        connected.add(edge.sourceHandle);
      }
      if (edge.target === id && edge.targetHandle) {
        connected.add(edge.targetHandle);
      }
    }
    return connected;
  }, [edges, id]);

  const storageKey = `expandedGroups:${node.id}`;
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const updateReactFlowHandles = useUpdateReactFlowHandles();

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      localStorage.setItem(storageKey, JSON.stringify([...next]));
      return next;
    });
  };

  useEffect(() => {
    updateReactFlowHandles(id);
  }, [vizHeight, id, updateReactFlowHandles, expandedGroups, isNodeCollapsed]);

  const sortedEndpoints = sortEndpoints(Object.entries(node.endpoints));

  const filteredEndpoints = useMemo(() => {
    if (!isNodeCollapsed) return sortedEndpoints;
    return sortedEndpoints.filter(([name, endpoint]) =>
      connectedEndpoints.has(name) || endpoint.isExposed || endpoint.primary
    );
  }, [sortedEndpoints, isNodeCollapsed, connectedEndpoints]);

  const groupedItems = groupEndpoints(filteredEndpoints);

  // Determine which group each endpoint belongs to (for collapse check)
  const getEndpointGroup = (itemIndex: number): string | undefined => {
    for (let i = itemIndex - 1; i >= 0; i--) {
      const item = groupedItems[i];
      if (item.kind === 'group') return item.name;
    }
    return undefined;
  };


  // Calculate handle position for a given item index, accounting for group headers, collapsed groups, and expanded configs
  const getHandleTop = (itemIndex: number) => {
    let offset = HEADER_HEIGHT + vizHeight;
    let currentGroup: string | undefined = undefined;
    let groupHeaderPosition = 0;

    for (let i = 0; i <= itemIndex; i++) {
      const item = groupedItems[i];

      if (item.kind === 'group') {
        currentGroup = item.name;
        groupHeaderPosition = offset + (ROW_HEIGHT / 2);
        if (i === itemIndex) return groupHeaderPosition;
        offset += ROW_HEIGHT;
      } else {
        const isCollapsed = currentGroup && !expandedGroups.has(currentGroup);
        if (isCollapsed) {
          // Collapsed endpoints align with their group header
          if (i === itemIndex) return groupHeaderPosition;
        } else {
          if (i === itemIndex) return offset + (ROW_HEIGHT / 2);
          offset += ROW_HEIGHT;
        }
      }
    }
    return offset;
  };

  return (
    <div style={{
      background: '#1f2937',
      border: selected ? '1px solid #3b82f6' : '1px solid #374151',
      borderRadius: 8,
      minWidth: 200,
      color: 'white',
      fontSize: 12,
      position: 'relative',
      boxShadow: selected ? '0 0 12px rgba(59, 130, 246, 0.4)' : undefined
    }}>
      <GraphNodeHeader
        nodeId={node.id}
        nodeType={node.type}
        label={node.label}
        info={node.info}
        isCollapsed={isNodeCollapsed}
        onToggleCollapse={toggleNodeCollapsed}
      />

      {!!vizRegistration && <NodeVisualization
        width={NODE_WIDTH}
        setHeight={setVizHeight}
        Viz={vizRegistration.component}
        getProps={vizRegistration.getProps}
        instanceId={instanceId}
        nodeId={node.id} />
      }

      {/* Endpoints */}
      {groupedItems.length > 0 ? (
        <div>
          {groupedItems.map((item, index) => {
            if (item.kind === 'group') {
              const isCollapsed = !expandedGroups.has(item.name);
              return (
                <div
                  key={`group-${item.name}`}
                  onClick={() => toggleGroup(item.name)}
                  style={{
                    height: ROW_HEIGHT,
                    padding: '0 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    color: '#9ca3af',
                    fontSize: 10,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #333',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <span style={{
                    display: 'inline-block',
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease'
                  }}>▼</span>
                  {item.name}
                </div>
              );
            }
            const group = getEndpointGroup(index);
            const isCollapsed = group && !expandedGroups.has(group);
            return (
              <div
                key={item.name}
                style={{
                  // Collapsed groups animate to 0, otherwise auto height
                  display: 'grid',
                  gridTemplateRows: isCollapsed ? '0fr' : '1fr',
                  transition: 'grid-template-rows 0.15s ease'
                }}
              >
                <div style={{ overflow: 'hidden' }}>
                  <CompactErrorBoundary name={`${node.id}.${item.name}`}>
                    <EndpointRow
                      endpoint={item.endpoint}
                      handleTop={getHandleTop(index)}
                      instanceId={instanceId}
                      nodeId={node.id}
                      nodeType={node.type}
                      isCollapsed={!!isCollapsed}
                      hasConnections={connectedEndpoints.has(item.name)}
                    />
                  </CompactErrorBoundary>
                </div>
              </div>
            );
          })}
        </div>
      ) : Object.keys(node.endpoints).length === 0 ? (
        <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: 11 }}>
          No endpoints
        </div>
      ) : null}
    </div>
  );
}

export const GraphNode = memo(GraphNodeComponent);
